import { useFacility } from '@/contexts/facility-context/hook';
import { useSet } from '@mantine/hooks';
import React, { createContext, ReactNode, useCallback, useState } from 'react';
import {
	AssetStatus,
	FacilityBagScanInput,
	MaterialType,
} from '../../../../../../backend/src/types.generated';
import {
	useFacilityBagScanMutation,
	useListTrackableAssetsForFacilitiesBagScanLazyQuery,
} from './graphql.generated';

// Define error types for bag scan workflow
export type BagScanError =
	| 'INVALID_BAG_STATE'
	| 'BAG_NOT_FOUND'
	| 'NETWORK_ERROR'
	| 'SUBMISSION_FAILED'
	| 'UNAUTHORIZED'
	| 'DUPLICATE_SCAN'
	| 'BAG_ALREADY_PROCESSED';

// Define the shape of your context state
export interface BagScanContextValue {
	status: BagScanStatus;
	page: BagScanPageType;
	setPage: React.Dispatch<React.SetStateAction<BagScanPageType>>;
	handleScan: (code: string) => void;
	evaluatingBag: {
		id: string;
		assetType: string;
		scannableCode: string;
		intendedMaterial: MaterialType | null;
		status?: AssetStatus | null | undefined;
		shouldConfirmMaterial: boolean;
	} | null;
	handleBagAction: (action: 'ACCEPT' | 'REJECT' | 'AUDIT', material: MaterialType) => Promise<void>;
	isSubmittingAction: boolean;
	sessionState: {
		accepted: number;
		rejected: number;
		audited: number;
	};
	// Error state management
	error: BagScanError | null;
	setError: React.Dispatch<React.SetStateAction<BagScanError | null>>;
}

export type BagScanPageType = 'SCAN' | 'MANUAL' | 'CONFIRM_MATERIAL' | 'EVALUATE';

export type BagScanStatus =
	| 'PENDING'
	| 'VERIFYING'
	| 'CONFIRMED'
	| 'DUPLICATE'
	| 'ERROR'
	| 'INVALID'
	| null;

// Create the context with undefined as default
// eslint-disable-next-line react-refresh/only-export-components
export const BagScanContext = createContext<BagScanContextValue | undefined>(undefined);

// Provider component props
interface BagScanContextProviderProps {
	children: ReactNode;
}

// Provider component
export const BagScanContextProvider: React.FC<BagScanContextProviderProps> = ({ children }) => {
	const [page, setPage] = useState<BagScanPageType>('SCAN');
	const [status] = useState<BagScanStatus>(null);
	const [error, setError] = useState<BagScanError | null>(null);
	const { currentFacility } = useFacility();
	const [sessionState, setSessionState] = useState<{
		accepted: number;
		rejected: number;
		audited: number;
	}>({
		accepted: 0,
		rejected: 0,
		audited: 0,
	});
	const [getTrackableAssets] = useListTrackableAssetsForFacilitiesBagScanLazyQuery();
	const [facilityBagScan, { loading: isSubmittingAction }] = useFacilityBagScanMutation({
		onCompleted: (data) => {
			if (data.facilityBagScan?.id) {
				setPage('SCAN');
			}
		},
	});
	const ignoredCodes = useSet();
	const [evaluatingBag, setEvaluatingBag] = useState<{
		id: string;
		scannableCode: string;
		assetType: string;
		intendedMaterial: MaterialType | null;
		status?: AssetStatus | null | undefined;
		shouldConfirmMaterial: boolean;
	} | null>(null);

	const handleScan = async (code: string) => {
		try {
			// Clear any previous errors
			setError(null);

			// Check for duplicate scans in current session
			if (ignoredCodes.has(code)) {
				setError('DUPLICATE_SCAN');
				return;
			}

			ignoredCodes.add(code);

			// Query API to get bag information
			const data = await getTrackableAssets({
				variables: {
					filter: {
						_or: [
							{ scannableCode: code.split('/').pop() },
							{ serialNumber: code.split('/').pop() },
						],
					},
				},
			}).then((res) => {
				if (res.data?.trackableAssets?.[0]) {
					return res.data.trackableAssets[0];
				}
				return null;
			});

			// Validate API response
			if (!data) {
				setError('BAG_NOT_FOUND');
				return;
			}

			// Check if bag has required properties
			if (!data.assetType) {
				setError('INVALID_BAG_STATE');
				return;
			}

			// Check bag status - only allow null or BAG_SCANNED status
			if (data.status !== null && data.status !== 'BAG_SCANNED') {
				setError('BAG_ALREADY_PROCESSED');
				return;
			}

			// Successfully scanned - set evaluating bag and navigate
			setEvaluatingBag({
				id: data.id,
				scannableCode: data.scannableCode ?? code,
				assetType: data.assetType,
				intendedMaterial: data.intendedMaterial ?? null,
				status: data.status,
				shouldConfirmMaterial: Boolean(data.shouldConfirmMaterial || !data.intendedMaterial),
			});
			setPage('EVALUATE');
		} catch {
			// Handle network errors or unexpected errors
			setError('NETWORK_ERROR');
		}
	};

	const handleBagAction = useCallback(
		async (action: 'ACCEPT' | 'REJECT' | 'AUDIT', material: MaterialType) => {
			if (!evaluatingBag || !currentFacility?.id) {
				setError('UNAUTHORIZED');
				return;
			}

			try {
				// Clear any previous errors
				setError(null);

				await facilityBagScan({
					variables: {
						input: {
							scannableCode: evaluatingBag.scannableCode,
							facilityId: currentFacility.id,
							material,
							action: action as FacilityBagScanInput['action'],
						},
					},
				});

				// Update session state on successful submission
				setSessionState((prev) => {
					const newState = {
						accepted: action === 'ACCEPT' ? prev.accepted + 1 : prev.accepted,
						rejected: action === 'REJECT' ? prev.rejected + 1 : prev.rejected,
						audited: action === 'AUDIT' ? prev.audited + 1 : prev.audited,
					};
					return newState;
				});
			} catch {
				// Handle submission errors
				setError('SUBMISSION_FAILED');
			}
		},
		[evaluatingBag, currentFacility?.id, facilityBagScan, setError]
	);

	return (
		<BagScanContext.Provider
			value={{
				status,
				page,
				setPage,
				handleScan,
				evaluatingBag,
				handleBagAction,
				isSubmittingAction,
				sessionState,
				error,
				setError,
			}}
		>
			{children}
		</BagScanContext.Provider>
	);
};
